import { useTranslation } from "react-i18next";
import { CATEGORY_META } from "../deviceCategories";
import { FreshValue } from "./FreshValue";
import { SetupStepBlock } from "./SetupStepBlock";
import type { SetupBoardRow, SetupBoardStatus, SetupWallRow } from "../buildSetupProgress";
import type { PlanSetupProgressProps } from "./types";

const STATUS_CLASS: Record<SetupBoardStatus, string> = {
  main: "bg-blue-600 text-white",
  considered: "bg-emerald-100 text-emerald-800",
  struck: "bg-slate-200 text-slate-500",
  open: "bg-amber-50 text-amber-700 border border-amber-200",
};

const WALL_ROW_LABEL: Record<SetupWallRow["key"], string> = {
  walls: "wallsFactWalls",
  doors: "wallsFactDoors",
  changed: "wallsFactChanged",
};

export const PlanSetupProgress = ({ progress, formatHeight, onHighlightTrayType }: PlanSetupProgressProps) => {
  const { t } = useTranslation();
  const { boards, heights, trays, walls } = progress;
  const empty = <span className="text-slate-300">—</span>;

  const boardDetails = (row: SetupBoardRow) => [
    row.fedFromName !== null && {
      key: "fedFrom",
      valueKey: row.fedFromName,
      text: t("smartSurveyor.cablesBeta.setupProgressFedFrom", { board: row.fedFromName }),
    },
    row.isOffFloor && {
      key: "offFloor",
      valueKey: "offFloor",
      text: t("smartSurveyor.cablesBeta.setupProgressOffFloor"),
    },
    row.feederCableType !== null && {
      key: "feeder",
      valueKey: row.feederCableType,
      text: t("smartSurveyor.cablesBeta.setupProgressFeeder", { type: row.feederCableType }),
    },
    row.feeds.length > 0 && {
      key: "feeds",
      valueKey: row.feeds.join(","),
      text: t("smartSurveyor.cablesBeta.setupProgressFeeds", {
        categories: row.feeds.map((category) => t(CATEGORY_META[category].i18nKey)).join(", "),
      }),
    },
    row.zoneCount > 0 && {
      key: "zones",
      valueKey: String(row.zoneCount),
      text: t("smartSurveyor.cablesBeta.setupProgressZones", { count: row.zoneCount }),
    },
  ].filter((detail): detail is { key: string; valueKey: string; text: string } => Boolean(detail));

  return (
    <section
      data-testid="plan-setup-progress"
      className="flex min-h-[8rem] flex-1 flex-col gap-2 overflow-y-auto rounded-lg border border-slate-300 bg-white p-3 shadow-sm"
    >
      <div>
        <div className="text-[15px] font-semibold text-slate-900">
          {t("smartSurveyor.cablesBeta.planSetupTitle")}
        </div>
        <div className="text-[13px] leading-snug text-slate-500">
          {t("smartSurveyor.cablesBeta.setupProgressIntro")}
        </div>
      </div>

      <SetupStepBlock
        number={1}
        title={t("smartSurveyor.cablesBeta.setupProgressStep_boards")}
        state={boards.state}
        answeredCount={boards.answeredCount}
        totalCount={boards.rows.length}
      >
        {boards.rows.map((row) => (
          <div key={row.label} data-testid={`setup-board-${row.label}`} className="rounded-md bg-white/70 px-1 py-0.5">
            <div className="flex items-center gap-2">
              <span
                className={`min-w-0 flex-1 truncate text-[13.5px] font-medium ${
                  row.status === "struck" ? "text-slate-400 line-through" : "text-slate-800"
                }`}
              >
                {row.name}
              </span>
              <FreshValue valueKey={row.status === "open" ? null : row.status}>
                <span className={`rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${STATUS_CLASS[row.status]}`}>
                  {t(`smartSurveyor.cablesBeta.setupProgressStatus_${row.status}`)}
                </span>
              </FreshValue>
              {row.status !== "struck" && (
                <FreshValue valueKey={row.heightM === null ? null : String(row.heightM)} className="text-[12.5px] tabular-nums">
                  {row.heightM === null ? empty : formatHeight(row.heightM)}
                </FreshValue>
              )}
            </div>
            {boardDetails(row).length > 0 && (
              <div className="mt-0.5 flex flex-wrap gap-x-1 gap-y-0.5 pl-1 text-[12.5px] text-slate-600">
                {boardDetails(row).map((detail) => (
                  <FreshValue key={detail.key} valueKey={detail.valueKey}>
                    {detail.text}
                  </FreshValue>
                ))}
              </div>
            )}
          </div>
        ))}
      </SetupStepBlock>

      <SetupStepBlock
        number={2}
        title={t("smartSurveyor.cablesBeta.setupProgressStep_heights")}
        state={heights.state}
        answeredCount={heights.answeredCount}
        totalCount={heights.rows.length}
      >
        {heights.rows.map((row) => (
          <div key={row.key} data-testid={`setup-height-${row.key}`} className="flex items-center gap-2 px-1 text-[13px]">
            <span className="min-w-0 flex-1 truncate text-slate-700">
              {t(`smartSurveyor.cablesBeta.heightLevel_${row.key}`)}
            </span>
            {row.heightM === null ? (
              <span className="text-[12px] italic text-slate-400">
                {t("smartSurveyor.cablesBeta.setupProgressSuggestion", { height: formatHeight(row.suggestionM) })}
              </span>
            ) : null}
            <FreshValue valueKey={row.heightM === null ? null : String(row.heightM)} className="font-semibold tabular-nums text-slate-900">
              {row.heightM === null ? empty : formatHeight(row.heightM)}
            </FreshValue>
          </div>
        ))}
      </SetupStepBlock>

      <SetupStepBlock
        number={3}
        title={t("smartSurveyor.cablesBeta.setupProgressStep_trays")}
        state={trays.state}
        answeredCount={trays.answeredCount}
        totalCount={trays.rows.length}
      >
        {trays.rows.length === 0 && (
          <span className="px-1 text-[13px] text-slate-500">{t("smartSurveyor.cablesBeta.setupProgressNoTrays")}</span>
        )}
        {trays.rows.map((row) => (
          <div
            key={row.trayType}
            data-testid={`setup-tray-${row.trayType}`}
            onMouseEnter={() => onHighlightTrayType?.(row.trayType)}
            onMouseLeave={() => onHighlightTrayType?.(null)}
            className="flex items-center gap-2 rounded px-1 text-[13px] hover:bg-blue-50"
          >
            <span className="min-w-0 flex-1 truncate text-slate-700">{row.trayType}</span>
            {row.heightM === null ? (
              <span className="text-[12px] italic text-slate-400">
                {t("smartSurveyor.cablesBeta.setupProgressSuggestion", { height: formatHeight(row.suggestionM) })}
              </span>
            ) : null}
            <FreshValue valueKey={row.heightM === null ? null : String(row.heightM)} className="font-semibold tabular-nums text-slate-900">
              {row.heightM === null ? empty : formatHeight(row.heightM)}
            </FreshValue>
          </div>
        ))}
      </SetupStepBlock>

      {walls && (
        <SetupStepBlock
          number={4}
          title={t("smartSurveyor.cablesBeta.setupProgressStep_walls")}
          state={walls.state}
          answeredCount={walls.answeredCount}
          totalCount={0}
        >
          {walls.rows.map((row) => (
            <div key={row.key} data-testid={`setup-walls-${row.key}`} className="flex items-center gap-2 px-1 text-[13px]">
              <span className="min-w-0 flex-1 truncate text-slate-700">
                {t(`smartSurveyor.cablesBeta.${WALL_ROW_LABEL[row.key]}`)}
              </span>
              <FreshValue valueKey={String(row.count)} className="font-semibold tabular-nums text-slate-900">
                {row.count}
              </FreshValue>
            </div>
          ))}
        </SetupStepBlock>
      )}
    </section>
  );
};
